import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import AIWriteButton from "../components/AIWriteButton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, Image as ImageIcon, Video, Store, Trash2, Tag, DollarSign, Camera, Radio, MapPin, LocateFixed, Pencil, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { SalePill } from "../components/SalePill";
import PlanStatusBanner from "../components/PlanStatusBanner";

function VendorOnboarding({ onDone }) {
  const { refreshUser } = useAuth();
  const [form, setForm] = useState({
    business_name: "",
    bio: "",
    vendor_type: "food",
    location: "",
    location_lat: null,
    location_lng: null,
    avatar_url: null,
    payment_handles: { cashapp: "", venmo: "", zelle: "", paypal: "", in_person: "Yes — local pickup" },
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [locating, setLocating] = useState(false);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported on this device");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = Number(pos.coords.latitude.toFixed(6));
        const lng = Number(pos.coords.longitude.toFixed(6));
        setForm((f) => ({ ...f, location_lat: lat, location_lng: lng }));
        toast.success("Location captured — buyers near you can now find your shop");
        setLocating(false);
      },
      (err) => {
        toast.error(err.code === 1 ? "Permission denied. Enable location in your browser." : "Couldn't get location");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };

  const handleAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, avatar_url: data.path }));
      toast.success("Avatar uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Avatar upload failed");
    } finally {
      setUploadingAvatar(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/vendors/become", form);
      await refreshUser();
      toast.success("You're a vendor now!");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create profile");
    } finally {
      setSubmitting(false);
    }
  };

  const setHandle = (k, v) =>
    setForm((f) => ({ ...f, payment_handles: { ...f.payment_handles, [k]: v } }));

  return (
    <form onSubmit={submit} className="max-w-2xl mx-auto px-4 py-6 space-y-5" data-testid="vendor-onboarding">
      <div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter">Open your shop.</h1>
        <p className="text-zinc-400 mt-1">Set up once. Sell forever.</p>
      </div>

      <div className="space-y-2">
        <Label className="text-zinc-300">Shop avatar</Label>
        <div className="flex items-center gap-4">
          <label
            className="relative w-20 h-20 rounded-full bg-white/5 border-2 border-dashed border-white/15 hover:border-pink-400 cursor-pointer overflow-hidden grid place-items-center transition-colors"
            data-testid="vendor-avatar-upload"
          >
            {form.avatar_url ? (
              <img src={fileUrl(form.avatar_url)} alt="avatar" className="w-full h-full object-cover" />
            ) : (
              <Camera className="w-6 h-6 text-zinc-400" />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleAvatar}
              className="hidden"
              data-testid="vendor-avatar-input"
            />
          </label>
          <p className="text-xs text-zinc-400">
            {uploadingAvatar ? "Uploading…" : "Tap to upload your shop avatar"}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-zinc-300">Business name</Label>
        <Input
          required
          value={form.business_name}
          onChange={(e) => setForm({ ...form, business_name: e.target.value })}
          placeholder="Mama's Hot Tacos"
          className="h-12 bg-white/5 border-white/10 rounded-2xl text-white"
          data-testid="vendor-business-name"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-zinc-300">What do you sell?</Label>
        <Select value={form.vendor_type} onValueChange={(v) => setForm({ ...form, vendor_type: v })}>
          <SelectTrigger className="h-12 bg-white/5 border-white/10 rounded-2xl text-white" data-testid="vendor-type-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/10 text-white">
            <SelectItem value="food">Food (homemade, street, bakery, etc.)</SelectItem>
            <SelectItem value="wholesale">Wholesale (bulk goods, resale, supplies)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-zinc-300">Bio</Label>
          <AIWriteButton
            kind="vendor_bio"
            prompt={form.business_name || form.category || "local food vendor"}
            tone="warm"
            maxWords={55}
            onText={(t) => setForm({ ...form, bio: t })}
            testid="ai-write-vendor-bio"
          />
        </div>
        <Textarea
          value={form.bio}
          onChange={(e) => setForm({ ...form, bio: e.target.value })}
          placeholder="Tell folks what makes your shop special."
          className="bg-white/5 border-white/10 rounded-2xl text-white min-h-[88px]"
          data-testid="vendor-bio"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-zinc-300">Location</Label>
        <Input
          value={form.location}
          onChange={(e) => setForm({ ...form, location: e.target.value })}
          placeholder="Brooklyn, NY"
          className="h-12 bg-white/5 border-white/10 rounded-2xl text-white"
          data-testid="vendor-location"
        />
        <button
          type="button"
          onClick={useMyLocation}
          disabled={locating}
          className="w-full h-11 rounded-2xl bg-cyan-400/10 hover:bg-cyan-400/20 border border-cyan-400/30 text-cyan-200 text-sm font-bold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          data-testid="vendor-use-my-location-btn"
        >
          <LocateFixed className="w-4 h-4" />
          {locating
            ? "Getting your location…"
            : form.location_lat != null
            ? `Pinned · ${form.location_lat.toFixed(3)}, ${form.location_lng.toFixed(3)}`
            : "Use my current location (for Near Me)"}
        </button>
        {form.location_lat != null && (
          <p className="text-[11px] text-zinc-500 inline-flex items-center gap-1">
            <MapPin className="w-3 h-3" /> Buyers within range will see your shop in Near Me results.
          </p>
        )}
      </div>

      <div className="space-y-2 rounded-3xl border border-white/10 bg-white/[0.03] p-5">
        <p className="text-sm font-bold text-pink-300 uppercase tracking-widest mb-3">
          Payment handles
        </p>
        <p className="text-xs text-zinc-500 mb-3">
          We don't process payments — buyers see your handles and pay you directly.
        </p>
        {[
          ["cashapp", "Cash App ($Tag)"],
          ["venmo", "Venmo (@username)"],
          ["zelle", "Zelle (email/phone)"],
          ["paypal", "PayPal"],
          ["apple_pay", "Apple Pay (number/email)"],
          ["in_person", "In-person / pickup"],
        ].map(([k, label]) => (
          <div key={k} className="space-y-1.5">
            <Label className="text-xs text-zinc-400">{label}</Label>
            <Input
              value={form.payment_handles[k] || ""}
              onChange={(e) => setHandle(k, e.target.value)}
              placeholder={label}
              className="h-11 bg-white/5 border-white/10 rounded-xl text-white"
              data-testid={`payment-handle-${k}`}
            />
          </div>
        ))}
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="w-full h-13 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold glow-pink"
        data-testid="vendor-submit-btn"
      >
        {submitting ? "Setting up…" : "Open my shop"}
      </Button>
    </form>
  );
}

import { CATEGORY_OPTIONS, slugifyCategory } from "../lib/categories";
import CategoryIcon from "../components/CategoryIcon";

// Convert ISO datetime string to <input type="datetime-local"> compatible value
function _toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}
// Convert <input type="datetime-local"> value back to ISO string (or null when empty)
function _fromLocalInput(v) {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

function ProductForm({ onCreated, product = null, onSaved = null, isEdit = false }) {
  const initial = product || {};
  const [form, setForm] = useState({
    name: initial.name || "",
    description: initial.description || "",
    price: initial.price != null ? String(initial.price) : "",
    category: initial.category || "street_food",
    subcategory: initial.subcategory || "",
    stock: initial.stock != null ? initial.stock : 100,
    tags: Array.isArray(initial.tags) ? initial.tags.join(", ") : (initial.tags || ""),
    is_sold_out: !!initial.is_sold_out,
    is_limited: !!initial.is_limited,
    open_now: initial.open_now !== false,
    pickup_available: initial.pickup_available !== false,
    delivery_available: !!initial.delivery_available,
    sale_price: initial.sale_price != null && initial.sale_price !== "" ? String(initial.sale_price) : "",
    promo_label: initial.promo_label || "",
    promo_starts_at: _toLocalInput(initial.promo_starts_at),
    promo_ends_at: _toLocalInput(initial.promo_ends_at),
  });
  const [images, setImages] = useState(Array.isArray(initial.image_paths) ? initial.image_paths : []);
  const [videos, setVideos] = useState(Array.isArray(initial.video_paths) ? initial.video_paths : []);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPromo, setShowPromo] = useState(!!initial.sale_price);

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const newPaths = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newPaths.push(data.path);
      }
      setImages((prev) => [...prev, ...newPaths]);
      toast.success("Image(s) uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleVideoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Video too large (max 100MB)");
      e.target.value = "";
      return;
    }
    setUploadingVideo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setVideos((prev) => [...prev, data.path]);
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Video upload failed");
    } finally {
      setUploadingVideo(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const salePriceNum = form.sale_price !== "" ? parseFloat(form.sale_price) : null;
      const payload = {
        ...form,
        price: parseFloat(form.price),
        stock: parseInt(form.stock, 10),
        image_paths: images,
        video_paths: videos,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        sale_price: salePriceNum && salePriceNum > 0 ? salePriceNum : null,
        promo_label: form.promo_label || "",
        promo_starts_at: _fromLocalInput(form.promo_starts_at),
        promo_ends_at: _fromLocalInput(form.promo_ends_at),
      };
      if (isEdit && product?.product_id) {
        await api.put(`/products/${product.product_id}`, payload);
        toast.success("Listing updated");
        onSaved && onSaved();
      } else {
        await api.post("/products", payload);
        toast.success("Product live!");
        setForm({ name: "", description: "", price: "", category: form.category, subcategory: "", stock: 100, tags: "", is_sold_out: false, is_limited: false, open_now: true, pickup_available: true, delivery_available: false, sale_price: "", promo_label: "", promo_starts_at: "", promo_ends_at: "" });
        setImages([]);
        setVideos([]);
        onCreated && onCreated();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (isEdit ? "Failed to update" : "Failed to publish"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-3xl bg-white/[0.03] border border-white/10 p-5 space-y-4" data-testid={isEdit ? "product-edit-form" : "product-form"}>
      <h3 className="text-xl font-black tracking-tight">{isEdit ? "Edit listing" : "Drop something new"}</h3>

      <div className="space-y-2">
        <Label className="text-zinc-300">Photos</Label>
        <label
          className="block border-2 border-dashed border-white/15 hover:border-pink-400 rounded-2xl p-5 cursor-pointer text-center bg-white/[0.02]"
          data-testid="photo-upload-zone"
        >
          <ImageIcon className="w-6 h-6 mx-auto text-zinc-400 mb-2" />
          <span className="text-sm text-zinc-300">
            {uploading ? "Uploading…" : "Tap to upload product photos"}
          </span>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            className="hidden"
            data-testid="photo-upload-input"
          />
        </label>
        {images.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            {images.map((p, i) => (
              <div key={i} className="relative w-20 h-20 rounded-xl overflow-hidden border border-white/10">
                <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => setImages(images.filter((_, x) => x !== i))}
                  className="absolute top-1 right-1 w-6 h-6 grid place-items-center rounded-full bg-black/70"
                  aria-label="Remove image"
                >
                  <Trash2 className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label className="text-zinc-300 flex items-center gap-2">
          Video <span className="text-[10px] uppercase tracking-widest text-pink-300 font-bold">Brings the gem to life</span>
        </Label>
        <label
          className="block border-2 border-dashed border-white/15 hover:border-pink-400 rounded-2xl p-5 cursor-pointer text-center bg-white/[0.02]"
          data-testid="video-upload-zone"
        >
          <Video className="w-6 h-6 mx-auto text-zinc-400 mb-2" />
          <span className="text-sm text-zinc-300">
            {uploadingVideo ? "Uploading…" : "Tap to upload a 5–30s clip (max 100MB)"}
          </span>
          <input
            type="file"
            accept="video/*"
            onChange={handleVideoChange}
            className="hidden"
            data-testid="video-upload-input"
          />
        </label>
        {videos.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            {videos.map((p, i) => (
              <div key={i} className="relative w-32 h-32 rounded-xl overflow-hidden border border-white/10 bg-black">
                <video src={fileUrl(p)} className="w-full h-full object-cover" muted autoPlay loop playsInline />
                <button
                  type="button"
                  onClick={() => setVideos(videos.filter((_, x) => x !== i))}
                  className="absolute top-1 right-1 w-6 h-6 grid place-items-center rounded-full bg-black/70"
                  aria-label="Remove video"
                  data-testid={`remove-video-${i}`}
                >
                  <Trash2 className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-zinc-300">Name</Label>
          <Input
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Birria Tacos"
            className="h-11 bg-white/5 border-white/10 rounded-xl text-white"
            data-testid="product-name"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-300">Price (USD)</Label>
          <div className="relative">
            <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              required
              type="number"
              step="0.01"
              min="0"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              placeholder="14.99"
              className="pl-9 h-11 bg-white/5 border-white/10 rounded-xl text-white"
              data-testid="product-price"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-zinc-300">Category</Label>
          <Select
            value={CATEGORY_OPTIONS.some((c) => c.value === form.category) ? form.category : "__custom__"}
            onValueChange={(v) => {
              if (v === "__custom__") {
                // Don't change anything yet — let them type into the custom input
                if (CATEGORY_OPTIONS.some((c) => c.value === form.category)) {
                  setForm({ ...form, category: "" });
                }
              } else {
                setForm({ ...form, category: v });
              }
            }}
          >
            <SelectTrigger className="h-11 bg-white/5 border-white/10 rounded-xl text-white" data-testid="product-category">
              <SelectValue placeholder="Pick a category" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-white/10 text-white max-h-64">
              {CATEGORY_OPTIONS.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  <span className="inline-flex items-center gap-2">
                    <CategoryIcon slug={c.icon} size={14} strokeWidth={2.2} />
                    {c.name}
                  </span>
                </SelectItem>
              ))}
              <SelectItem value="__custom__">
                <span className="inline-flex items-center gap-2">
                  <Pencil className="w-3.5 h-3.5" />
                  Other (enter your own)
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
          {!CATEGORY_OPTIONS.some((c) => c.value === form.category) && (
            <Input
              value={form.category || ""}
              onChange={(e) => setForm({ ...form, category: slugifyCategory(e.target.value) })}
              placeholder="e.g. Late Night, Hibachi, Wings"
              maxLength={40}
              className="h-11 bg-white/5 border-white/10 rounded-xl text-white"
              data-testid="product-category-custom"
            />
          )}
        </div>
        <div className="space-y-2">
          <Label className="text-zinc-300">Stock</Label>
          <Input
            type="number"
            min="0"
            value={form.stock}
            onChange={(e) => setForm({ ...form, stock: e.target.value })}
            className="h-11 bg-white/5 border-white/10 rounded-xl text-white"
            data-testid="product-stock"
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-zinc-300">Description</Label>
          <AIWriteButton
            kind="product_description"
            prompt={form.name || form.category || "menu item"}
            tone="punchy"
            maxWords={45}
            onText={(t) => setForm({ ...form, description: t })}
            testid="ai-write-product-description"
          />
        </div>
        <Textarea
          required
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Slow-braised beef, melty cheese, crispy tortilla, side of consommé."
          className="bg-white/5 border-white/10 rounded-xl text-white min-h-[80px]"
          data-testid="product-description"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-zinc-300">Tags (comma separated)</Label>
        <div className="relative">
          <Tag className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={form.tags}
            onChange={(e) => setForm({ ...form, tags: e.target.value })}
            placeholder="spicy, mexican, lunch"
            className="pl-9 h-11 bg-white/5 border-white/10 rounded-xl text-white"
            data-testid="product-tags"
          />
        </div>
      </div>

      <div className="space-y-2 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold">Status</Label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { key: "open_now", label: "Open now", testid: "toggle-open-now" },
            { key: "is_sold_out", label: "Sold out", testid: "toggle-sold-out" },
            { key: "is_limited", label: "Limited qty", testid: "toggle-limited" },
            { key: "pickup_available", label: "Pickup", testid: "toggle-pickup" },
            { key: "delivery_available", label: "Delivery", testid: "toggle-delivery" },
          ].map((t) => (
            <label
              key={t.key}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer border transition-colors ${
                form[t.key] ? "bg-pink-500/15 border-pink-400/40" : "bg-white/[0.02] border-white/10"
              }`}
              data-testid={t.testid}
            >
              <input
                type="checkbox"
                checked={!!form[t.key]}
                onChange={(e) => setForm({ ...form, [t.key]: e.target.checked })}
                className="accent-pink-500"
              />
              <span className="text-sm font-semibold">{t.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-2 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <div className="flex items-center justify-between">
          <Label className="text-zinc-300 text-xs uppercase tracking-widest font-bold flex items-center gap-1.5">
            <Tag className="w-3.5 h-3.5" /> Promotion / Sale
          </Label>
          <button
            type="button"
            onClick={() => setShowPromo((v) => !v)}
            className="text-xs font-bold text-pink-300 hover:text-pink-200"
            data-testid="toggle-promo-section"
          >
            {showPromo ? "Hide" : "Add a sale →"}
          </button>
        </div>
        {showPromo && (
          <div className="space-y-3 pt-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Sale price (USD)</Label>
                <div className="relative">
                  <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.sale_price}
                    onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
                    placeholder="Lower than regular price"
                    className="pl-9 h-11 bg-white/5 border-white/10 rounded-xl text-white"
                    data-testid="product-sale-price"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Promo label (optional)</Label>
                <Input
                  value={form.promo_label}
                  onChange={(e) => setForm({ ...form, promo_label: e.target.value })}
                  placeholder="Weekend Special"
                  className="h-11 bg-white/5 border-white/10 rounded-xl text-white"
                  data-testid="product-promo-label"
                  maxLength={32}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Starts at (optional)</Label>
                <Input
                  type="datetime-local"
                  value={form.promo_starts_at}
                  onChange={(e) => setForm({ ...form, promo_starts_at: e.target.value })}
                  className="h-11 bg-white/5 border-white/10 rounded-xl text-white"
                  data-testid="product-promo-starts"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-400 text-xs">Ends at (optional)</Label>
                <Input
                  type="datetime-local"
                  value={form.promo_ends_at}
                  onChange={(e) => setForm({ ...form, promo_ends_at: e.target.value })}
                  className="h-11 bg-white/5 border-white/10 rounded-xl text-white"
                  data-testid="product-promo-ends"
                />
              </div>
            </div>
            {form.sale_price && form.price && parseFloat(form.sale_price) > 0 && parseFloat(form.sale_price) < parseFloat(form.price) && (
              <p className="text-xs text-emerald-300 font-bold">
                -{Math.round(((parseFloat(form.price) - parseFloat(form.sale_price)) / parseFloat(form.price)) * 100)}% off · Buyers will see a SALE pill
              </p>
            )}
            {form.sale_price && form.price && parseFloat(form.sale_price) >= parseFloat(form.price) && (
              <p className="text-xs text-amber-300">Sale price must be lower than the regular price</p>
            )}
          </div>
        )}
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold glow-pink"
        data-testid={isEdit ? "product-update-btn" : "product-submit-btn"}
      >
        {submitting ? (isEdit ? "Saving…" : "Publishing…") : (isEdit ? "Save changes" : "Publish gem")}
      </Button>
    </form>
  );
}

function ShopLocationPin({ user, refreshUser }) {
  const profile = user.vendor_profile || {};
  const [locating, setLocating] = useState(false);
  const hasPin = profile.location_lat != null && profile.location_lng != null;

  const setPin = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported on this device");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const payload = {
            ...profile,
            location_lat: Number(pos.coords.latitude.toFixed(6)),
            location_lng: Number(pos.coords.longitude.toFixed(6)),
          };
          await api.put("/vendors/profile", payload);
          await refreshUser();
          toast.success("Shop pinned — Near Me search is on");
        } catch (err) {
          toast.error(err.response?.data?.detail || "Couldn't save pin");
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        toast.error(err.code === 1 ? "Permission denied. Enable location in your browser." : "Couldn't get location");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  };

  const clearPin = async () => {
    try {
      const payload = { ...profile, location_lat: null, location_lng: null };
      await api.put("/vendors/profile", payload);
      await refreshUser();
      toast.success("Pin removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't remove pin");
    }
  };

  return (
    <div className="rounded-2xl p-4 bg-white/[0.03] border border-white/10 flex flex-wrap items-center gap-3" data-testid="shop-location-pin">
      <div className={`w-10 h-10 rounded-full grid place-items-center ${hasPin ? "bg-cyan-400/15 text-cyan-200 border border-cyan-400/30" : "bg-white/5 text-zinc-400 border border-white/10"}`}>
        <MapPin className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold">{hasPin ? "Shop pinned" : "Pin your shop for Near Me"}</p>
        <p className="text-xs text-zinc-400 truncate">
          {hasPin
            ? `Lat ${Number(profile.location_lat).toFixed(3)}, Lng ${Number(profile.location_lng).toFixed(3)} — buyers nearby can find you.`
            : "Share your GPS once. Show up in nearby searches and on the Near Me feed."}
        </p>
      </div>
      <div className="flex gap-2">
        <button
          onClick={setPin}
          disabled={locating}
          className="h-10 px-3 rounded-full bg-cyan-400/15 hover:bg-cyan-400/25 text-cyan-200 border border-cyan-400/30 text-xs font-bold inline-flex items-center gap-1 disabled:opacity-60"
          data-testid="vendor-update-pin-btn"
        >
          <LocateFixed className="w-3.5 h-3.5" />
          {locating ? "…" : hasPin ? "Update" : "Pin shop"}
        </button>
        {hasPin && (
          <button
            onClick={clearPin}
            className="h-10 px-3 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold"
            data-testid="vendor-clear-pin-btn"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

export default function Sell() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null); // product being edited

  useEffect(() => { refreshUser(); /* eslint-disable-next-line */ }, []);

  const loadData = async () => {
    if (!user?.is_vendor) return;
    try {
      const [pRes, oRes] = await Promise.all([
        api.get("/products", { params: { vendor_id: user.user_id, limit: 100 } }),
        api.get("/orders", { params: { role: "vendor" } }),
      ]);
      setProducts(pRes.data);
      setOrders(oRes.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, [user?.is_vendor]);

  const onDelete = async (id) => {
    if (!window.confirm("Take this down?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Removed");
      loadData();
    } catch {
      toast.error("Couldn't remove");
    }
  };

  if (!user) return null;
  if (!user.is_vendor) {
    return (
      <VendorOnboarding onDone={loadData} />
    );
  }
  // Vendor application pending admin review
  if (user.is_approved === false) {
    return (
      <div className="max-w-xl mx-auto px-4 py-10 space-y-6 text-center" data-testid="vendor-pending">
        <div className="mx-auto w-16 h-16 rounded-full bg-amber-400/15 grid place-items-center border border-amber-400/30">
          <Radio className="w-8 h-8 text-amber-300 animate-pulse" />
        </div>
        <div>
          <h1 className="text-3xl font-black tracking-tighter">Application under review</h1>
          <p className="text-sm text-zinc-400 mt-2">
            Thanks for applying! An admin is reviewing your shop now. We'll send you a notification the moment you're approved — usually within 24 hours.
          </p>
        </div>
        <div className="rounded-3xl p-5 bg-gradient-to-br from-amber-400/10 to-transparent border border-amber-400/30 text-left space-y-1.5">
          <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">What you submitted</p>
          <p className="font-black text-lg">{user.vendor_profile?.business_name}</p>
          {user.vendor_profile?.bio && <p className="text-sm text-zinc-300">{user.vendor_profile.bio}</p>}
          <p className="text-xs text-zinc-400">{user.vendor_profile?.vendor_type} · {user.vendor_profile?.location || "Local"}</p>
        </div>
        <p className="text-xs text-zinc-500">In the meantime, you can still browse, post in Community, and order from other vendors.</p>
      </div>
    );
  }
  if (user.is_suspended) {
    return (
      <div className="max-w-xl mx-auto px-4 py-10 space-y-4 text-center" data-testid="vendor-suspended">
        <div className="mx-auto w-16 h-16 rounded-full bg-red-500/15 grid place-items-center border border-red-400/30">
          <Radio className="w-8 h-8 text-red-300" />
        </div>
        <h1 className="text-3xl font-black tracking-tighter">Your shop is suspended</h1>
        <p className="text-sm text-zinc-400">Reach out to support via your Profile → Customer Support.</p>
      </div>
    );
  }

  const totalRevenue = orders
    .filter((o) => ["paid", "shipped", "completed"].includes(o.status))
    .reduce((s, o) => s + (o.total || 0), 0);

  return (
    <div className="max-w-5xl mx-auto px-4 py-5 space-y-5" data-testid="vendor-dashboard">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-3xl font-black tracking-tighter">Your shop</h1>
          <p className="text-sm text-zinc-400 truncate">@{user.vendor_profile?.business_name}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/sell/insights")}
            className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-100 border border-cyan-400/30 font-bold text-sm"
            data-testid="open-insights-btn"
          >
            <Sparkles className="w-4 h-4" /> Insights
          </button>
          <button
            onClick={() => navigate("/sell/shop")}
            className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-white/10 hover:bg-white/15 text-white font-bold text-sm"
            data-testid="edit-shop-btn"
          >
            <Pencil className="w-4 h-4" /> Edit shop
          </button>
          <button
            onClick={() => navigate("/golive")}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-red-500 hover:bg-red-400 text-white font-bold pulse-glow"
            data-testid="vendor-go-live-btn"
          >
            <Radio className="w-4 h-4" /> Go Live
          </button>
        </div>
      </div>

      <PlanStatusBanner />

      {/* Stats bento */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-2xl p-4 bg-gradient-to-br from-pink-500/15 to-transparent border border-pink-400/20">
          <p className="text-[10px] uppercase tracking-widest text-pink-300 font-bold">Products</p>
          <p className="mt-1 text-3xl font-black" data-testid="stat-products">{products.length}</p>
        </div>
        <div className="rounded-2xl p-4 bg-gradient-to-br from-cyan-400/15 to-transparent border border-cyan-400/20">
          <p className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold">Orders</p>
          <p className="mt-1 text-3xl font-black" data-testid="stat-orders">{orders.length}</p>
        </div>
        <div className="rounded-2xl p-4 bg-gradient-to-br from-emerald-400/15 to-transparent border border-emerald-400/20">
          <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold">Revenue</p>
          <p className="mt-1 text-3xl font-black" data-testid="stat-revenue">${totalRevenue.toFixed(0)}</p>
        </div>
      </div>

      <ShopLocationPin user={user} refreshUser={refreshUser} />

      <ProductForm onCreated={loadData} />

      {/* Product list */}
      <div className="space-y-3">
        <h3 className="text-lg font-bold">Listings</h3>
        {products.length === 0 && (
          <div className="text-center py-10 text-zinc-500 border border-dashed border-white/10 rounded-3xl" data-testid="no-products">
            <Plus className="w-6 h-6 mx-auto mb-2" />
            No products yet.
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {products.map((p) => (
            <div key={p.product_id} className="relative rounded-2xl overflow-hidden bg-zinc-900 border border-white/5" data-testid={`my-product-${p.product_id}`}>
              <div className="aspect-square bg-zinc-800 relative">
                {p.image_paths?.[0] && (
                  <img src={fileUrl(p.image_paths[0])} alt={p.name} className="w-full h-full object-cover" />
                )}
                <div className="absolute top-2 left-2">
                  <SalePill product={p} />
                </div>
              </div>
              <div className="p-3 space-y-1">
                <p className="font-bold truncate">{p.name}</p>
                <div className="flex items-baseline gap-1.5">
                  {p.sale_price && Number(p.sale_price) > 0 && Number(p.sale_price) < Number(p.price) ? (
                    <>
                      <p className="text-pink-400 font-black">${Number(p.sale_price).toFixed(2)}</p>
                      <p className="text-zinc-500 text-xs line-through">${Number(p.price).toFixed(2)}</p>
                    </>
                  ) : (
                    <p className="text-pink-400 font-black">${Number(p.price).toFixed(2)}</p>
                  )}
                </div>
                <div className="flex items-center gap-3 pt-1">
                  <button
                    onClick={() => setEditing(p)}
                    className="inline-flex items-center gap-1 text-xs text-cyan-300 hover:text-cyan-200 font-bold"
                    data-testid={`edit-product-${p.product_id}`}
                  >
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button
                    onClick={() => onDelete(p.product_id)}
                    className="text-xs text-red-400 hover:text-red-300"
                    data-testid={`delete-product-${p.product_id}`}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit listing sheet */}
      <Sheet open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-2xl bg-zinc-950 border-l border-white/10 text-white overflow-y-auto" data-testid="edit-product-sheet">
          <SheetHeader>
            <SheetTitle className="text-white">Edit listing</SheetTitle>
          </SheetHeader>
          {editing && (
            <div className="mt-4">
              <ProductForm
                product={editing}
                isEdit
                onSaved={() => { setEditing(null); loadData(); }}
              />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
